// THE THEME SWITCH, and the only piece of reader state in this application that
// is not in the URL.
//
// Everything else here — a lens, a step, a filter, a search, a cast — travels in
// the address, because every one of those is part of what a page SAYS and a link
// to it should arrive saying the same thing. A theme is not: it is a fact about
// the person reading, not about the page, and a link that forced the recipient
// into cream would be the URL overreaching. So it lives in localStorage, per
// browser, and a shared link arrives in whatever theme the reader already uses.
//
// The same try/catch bargain correspondences/trail.js makes: a private window or
// a browser refusing storage outright costs the reader their preference and
// nothing else, because the dark theme is the default and the switch still works
// for the length of the visit.
const KEY = "theme";
const THEMES = ["dark", "light"];
const DEFAULT = "dark";

export function readTheme(storage) {
  try {
    // Dereferenced INSIDE the try, and that is the whole point. As a default
    // parameter (`storage = localStorage`) this threw before the function body
    // began, which is to say outside the very try/catch written to catch it: on
    // a browser that denies storage the getter raises on ACCESS, not on
    // `getItem`, so the switch stopped working altogether on the one
    // configuration this bargain exists for. A browser test caught it.
    const stored = (storage || localStorage).getItem(KEY);
    return THEMES.includes(stored) ? stored : DEFAULT;
  } catch {
    // See the note above. Nothing is broken; the default stands.
    return DEFAULT;
  }
}

export function writeTheme(theme, storage) {
  try {
    // Inside the try, for the reason readTheme explains.
    (storage || localStorage).setItem(KEY, theme);
  } catch {
    /* see readTheme */
  }
}

export function other(theme) {
  return theme === "light" ? "dark" : "light";
}

// The attribute on <html> is what the stylesheet reads, and it is the ONE place
// the current theme is written down. The switch's own appearance is derived from
// it in CSS rather than from a class of its own, so the control cannot come to
// disagree with the page it is controlling — the same reason the alphabet page
// reads its mapping out of the rendered table.
export function applyTheme(theme, root = document.documentElement) {
  root.dataset.theme = theme;
}

// The server cannot know the theme: it is in the reader's browser and is
// deliberately never sent. So the markup arrives making no claim, and the state
// is written here. The THUMB is already in the right place before this runs,
// because CSS positions it from `html[data-theme]`, which the inline script in
// the head set before the first paint; what this fixes is the ACCESSIBLE state,
// which no stylesheet can set.
function sync(button, theme) {
  button.setAttribute("aria-checked", String(theme === "light"));
  const label = `Switch to the ${other(theme)} theme`;
  button.setAttribute("aria-label", label);
  button.setAttribute("title", label);
}

function wire(button) {
  sync(button, readTheme());

  button.addEventListener("click", () => {
    const next = other(readTheme());
    applyTheme(next);
    writeTheme(next);
    sync(button, next);
  });
}

export function start() {
  document.querySelectorAll("[data-theme-switch]:not([data-theme-switch-ready])")
    .forEach((button) => {
      button.dataset.themeSwitchReady = "true";
      wire(button);
    });
}

// Turbo replaces the body on every visit, so the button that arrives is a new
// element with the server's stateless markup on it — without re-running this, a
// reader in the light theme would see a switch claiming to be off from the
// second page onwards. The attribute on <html> survives, because Turbo does not
// replace <html>; it is only the control that needs saying again.
document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
